/**
 * Component for a MiniHeader
 * Display component for the collapsed header state that displays on some pages
    after scrolling past the normal header. Animates itself in and out
 *
 * @module component/MiniHeader
 */

import PropTypes from 'prop-types';
import React, { Children } from 'react';
import ReactCSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';

import CloseIcon from '../../../../static/icons/close.svg';
import Icon from '../../../components/Icon/Icon';
import { extractRGBAstring, isColorDark, isRGBADark } from '../../../lib/ArtUtils';
import { createBEM } from '../../../lib/utils/css';
import Logger from '../../../tools/Logger';

import './MiniHeader.scss';

const log = Logger.child({ component: 'MiniHeader' }, true);

export default class extends React.Component {
  /**
   * @property {boolean} showing - controls showing/hiding. Transition animated
   * @property {string} color - background color of miniHeader, takes an rgba string
   */
  static propTypes = {
    showing: PropTypes.bool,
    color: PropTypes.string,
    onClose: PropTypes.func,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  };

  static defaultProps = {
    color: 'rgba(0,173,238,1)',
    showing: false,
  };

  render() {
    let miniHeader = null;

    if (this.props.showing) {
      const color = this.props.color;
      const colorRgba = extractRGBAstring(color);
      let isDark;

      if (colorRgba) {
        isDark = isRGBADark(colorRgba);
      } else {
        isDark = isColorDark(color);
        log.error(`tried to render with a unsupported color format: ${color}`);
      }

      const miniHeaderClass = createBEM('MiniHeader', {
        light: !isDark,
        dark: isDark,
      });

      const miniHeaderStyle = {
        backgroundColor: color,
      };

      let maybeCloseButton = null;
      if (this.props.onClose) {
        maybeCloseButton = (
          <div className='MiniHeader__dismiss' onClick={this.props.onClose} role='button'>
            <Icon className='icon-close' glyph={CloseIcon} />
          </div>
        );
      }

      miniHeader = (
        <div className={miniHeaderClass} style={miniHeaderStyle}>
          {Children.map(this.props.children, (child, index) => {
            if (!child) {
              return null;
            }
            return (
              <div key={index} className='MiniHeader__contentItem'>
                {React.cloneElement(child)}
              </div>
            );
          })}
          {maybeCloseButton}
        </div>
      );
    }

    return (
      <ReactCSSTransitionGroup
        className='MiniHeaderWrapper'
        transitionName='fx-miniHeaderSlide'
        transitionEnterTimeout={250}
        transitionLeaveTimeout={250}
      >
        {miniHeader}
      </ReactCSSTransitionGroup>
    );
  }
}
